// src/layout/MainLayout.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { NavLink, useLocation, Outlet, useNavigate } from "react-router-dom";
import "./MainLayout.css";
import { authFetch } from "../utils/authFetch";
import NotificacionSoporte from "../components/NotificacionSoporte";

// Iconos: uno solo importado desde el mapa compartido. Antes venían de
// cinco librerías distintas, cada uno con su tamaño y su color fijo, y el
// resto del menú usaba emojis (que se dibujan distinto en cada sistema).
import { Iconos } from "../componentes/base/iconos";

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const rol = user?.rol || "";
  const puede = (roles) => roles.includes(rol);

  // Desktop collapse
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });

  // Mobile drawer open
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Clientes esperando asesor: contador en el menú + globo de aviso
  const [soportePend, setSoportePend] = useState(0);
  const [avisos, setAvisos] = useState([]);
  const vistosRef = useRef(null);

  // Beep corto generado en el navegador (sin archivos de audio)
  function sonarAviso() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1180, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.42);
      osc.start(); osc.stop(ctx.currentTime + 0.45);
    } catch {}
  }

  useEffect(() => {
    if (!["admin", "caja"].includes(rol)) return;

    // Conversaciones ya avisadas (sobrevive a recargas de la pestaña)
    if (vistosRef.current === null) {
      try {
        vistosRef.current = new Set(
          JSON.parse(sessionStorage.getItem("soporte_avisados") || "[]")
        );
      } catch { vistosRef.current = new Set(); }
    }

    let vivo = true;
    async function revisar() {
      try {
        const res = await authFetch("/api/soporte/conversaciones?estado=pendiente");
        const data = await res.json();
        if (!vivo || !data.ok) return;

        const pendientes = data.conversaciones || [];
        setSoportePend(pendientes.length);

        const nuevas = pendientes.filter((c) => !vistosRef.current.has(c.id));
        if (nuevas.length > 0) {
          nuevas.forEach((c) => vistosRef.current.add(c.id));
          try {
            sessionStorage.setItem(
              "soporte_avisados",
              JSON.stringify([...vistosRef.current])
            );
          } catch {}
          setAvisos((prev) => [...prev, ...nuevas].slice(-3)); // máx 3 en pantalla
          sonarAviso();
        }
      } catch {}
    }

    revisar();
    const t = setInterval(revisar, 15000);
    return () => { vivo = false; clearInterval(t); };
  }, [rol]);

  function abrirConversacion(id) {
    setAvisos((prev) => prev.filter((c) => c.id !== id));
    navigate(`/soporte?conv=${id}`);
  }

  useEffect(() => {
    try {
      localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  // Cierra drawer al navegar en móvil
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Cierra con ESC
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setDrawerOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function toggleSidebar() {
    setCollapsed((v) => !v);
  }

  function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "";
  }

  const titulo = useMemo(() => {
    const p = location.pathname;
    if (p === "/") return "Dashboard";
    if (p.startsWith("/onus-registradas")) return "ONUs Registradas";
    if (p.startsWith("/onus-olt")) return "Importar ONUs OLT";
    if (p.startsWith("/onus-no-autorizadas")) return "ONUs No Autorizadas";
    if (p.startsWith("/clientes")) return "Clientes";
    if (p.startsWith("/caja")) return "Caja";
    if (p.startsWith("/pagos")) return "Pagos";
    if (p.startsWith("/estado-cuenta")) return "Estado de Cuenta";
    if (p.startsWith("/respaldos")) return "Respaldos de equipos";
    if (p.startsWith("/soporte")) return "Soporte";
    if (p.startsWith("/cortes")) return "Cortes";
    if (p.startsWith("/planes")) return "Planes";
    if (p.startsWith("/empresa")) return "Empresa";
    if (p.startsWith("/usuarios")) return "Usuarios";
    if (p.startsWith("/olt-config")) return "OLT Config";
    if (p.startsWith("/planta")) return "Planta externa";
    if (p.startsWith("/mikrotik")) return "MikroTik Config";
    return "WIFIMEX";
  }, [location.pathname]);

  const subtitulo = useMemo(() => {
    if (!user) return "Bienvenido.";
    return `Bienvenido, ${user.nombre || user.usuario}.`;
  }, [user]);

  const iniciales = useMemo(() => {
    const n = (user?.nombre || user?.usuario || "").trim();
    if (!n) return "WF";
    const parts = n.split(/\s+/);
    const a = parts[0]?.[0] || "W";
    const b = parts[1]?.[0] || parts[0]?.[1] || "F";
    return (a + b).toUpperCase();
  }, [user]);

  return (
    <div className={`layout ${collapsed ? "sidebar-collapsed" : ""}`}>
      <NotificacionSoporte
        items={avisos}
        onAbrir={abrirConversacion}
        onDescartar={(id) => setAvisos((prev) => prev.filter((c) => c.id !== id))}
      />

      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${drawerOpen ? "open" : ""}`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* SIDEBAR */}
      <aside className={`sidebar ${drawerOpen ? "drawer-open" : ""}`}>
        <div className="sidebar-brand">
          <div className="brand-logo">WF</div>

          <div className="brand-text">
            <span className="brand-title">WIFIMEX</span>
            <span className="brand-subtitle">ISP Panel</span>
          </div>

          {/* Toggle desktop */}
          <button
            className="sidebar-toggle desktop-only"
            onClick={toggleSidebar}
            type="button"
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {collapsed
              ? <Iconos.siguiente size={17} strokeWidth={2} />
              : <Iconos.atras size={17} strokeWidth={2} />}
          </button>

          {/* Close mobile */}
          <button
            className="sidebar-close mobile-only"
            onClick={() => setDrawerOpen(false)}
            type="button"
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          <p className="sidebar-section-title">Principal</p>

          <NavLink
            to="/"
            end
            className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}
            title="Dashboard"
          >
            <Iconos.panel className="icon" size={18} strokeWidth={1.8} />
            <span className="link-text">Dashboard</span>
          </NavLink>

          {puede(["admin", "tecnicos", "caja"]) && (
            <>
              <p className="sidebar-section-title">ONU</p>

              <NavLink to="/onus-registradas" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="ONUs Registradas">
                <Iconos.onus className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">ONUs Registradas</span>
              </NavLink>

              <NavLink to="/onus-olt" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Importar ONUs OLT">
                <Iconos.importar className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">Importar ONUs OLT</span>
              </NavLink>

              <NavLink to="/onus-no-autorizadas" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="ONUs No Autorizadas">
                <Iconos.aviso className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">ONUs No Autorizadas</span>
              </NavLink>
            </>
          )}

          {puede(["admin", "tecnicos"]) && (
            <>
              <p className="sidebar-section-title">Red</p>

              {puede(["admin"]) && (
                <>
                  <NavLink to="/olt-config" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="OLT Config">
                    <Iconos.olt className="icon" size={18} strokeWidth={1.8} />
                    <span className="link-text">OLT Config</span>
                  </NavLink>

                  <NavLink to="/mikrotik" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="MikroTik Config">
                    <Iconos.antena className="icon" size={18} strokeWidth={1.8} />
                    <span className="link-text">MikroTik Config</span>
                  </NavLink>
                </>
              )}

              <NavLink to="/switches" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Switches">
                <Iconos.equipos className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">Switches</span>
              </NavLink>

              <NavLink to="/planta" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Planta externa: fibra, cajas y cierres en el mapa">
                <Iconos.ubicacion className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">Planta externa</span>
              </NavLink>
            </>
          )}

          <p className="sidebar-section-title">Administración</p>

          {puede(["admin", "caja", "tecnicos"]) && (
            <NavLink to="/clientes" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Clientes">
              <Iconos.clientes className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Clientes</span>
            </NavLink>
          )}

          {puede(["admin", "caja"]) && (
            <NavLink to="/caja" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Caja">
              <Iconos.caja className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Corte de Caja</span>
            </NavLink>
          )}

          {puede(["admin"]) && (
            <NavLink to="/pagos" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Pagos">
              <Iconos.pagos className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Consultar Pagos</span>
            </NavLink>
          )}

          {puede(["admin", "caja"]) && (
            <NavLink to="/estado-cuenta" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Estado de Cuenta PDF">
              <Iconos.documento className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Estado de Cuenta PDF</span>
            </NavLink>
          )}

          <p className="sidebar-section-title">Operación</p>

          {puede(["admin", "caja"]) && (
            <NavLink to="/cortes" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Cortes">
              <Iconos.tijeras className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Cortes</span>
            </NavLink>
          )}

          {puede(["admin", "caja"]) && (
            <NavLink to="/corteshistorial" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Historial">
              <Iconos.historial className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Historial</span>
            </NavLink>
          )}

          {puede(["admin"]) && (
            <NavLink to="/respaldos" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Respaldos de equipos">
              <Iconos.respaldos className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Respaldos</span>
            </NavLink>
          )}

          {puede(["admin", "caja"]) && (
            <NavLink to="/soporte" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Soporte / chat con clientes">
              <Iconos.soporte className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">
                Soporte
                {soportePend > 0 && <span className="sidebar-badge">{soportePend}</span>}
              </span>
            </NavLink>
          )}

          <p className="sidebar-section-title">Configuración</p>
          <NavLink to="/empresa" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Empresa">
              <Iconos.empresa className="icon" size={18} strokeWidth={1.8} />
              <span className="link-text">Empresa</span>
          </NavLink>

          {puede(["admin"]) && (
            <>

              <NavLink to="/planes" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Planes">
                <Iconos.planes className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">Planes</span>
              </NavLink>

              <NavLink to="/usuarios" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")} title="Usuarios">
                <Iconos.usuarios className="icon" size={18} strokeWidth={1.8} />
                <span className="link-text">Usuarios</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <span className="sidebar-footer-text">© WIFIMEX {new Date().getFullYear()}</span>
        </div>
      </aside>

      {/* CONTENIDO */}
      <div className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            {/* Mobile hamburger */}
            <button
              className="hamburger mobile-only"
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menú"
              title="Menú"
            >
              ☰
            </button>

            <div>
              <h1 className="topbar-title">{titulo}</h1>
              <p className="topbar-subtitle">{subtitulo}</p>
            </div>
          </div>

          <div className="topbar-right">
            <div className="topbar-search desktop-only">
              <span>🔍</span>
              <input type="text" placeholder="Buscar..." className="topbar-search-input" />
            </div>

            <div className="topbar-user">
              <div className="user-avatar">{iniciales}</div>
              <div className="user-info desktop-only">
                <span className="user-name">{user?.nombre || user?.usuario || "Usuario"}</span>
                <span className="user-role">{rol || "-"}</span>
              </div>

              <button onClick={logout} className="logout-btn" type="button">
                Salir
              </button>
            </div>
          </div>
        </header>

        {/* 👇 AQUÍ VA EL CONTENIDO DE LA RUTA HIJA */}
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}